import { useEffect, useMemo, useState } from 'react';
import { Globe, Lock, X } from 'lucide-react';
import type { Area, Assignee } from '@cg-projects/shared';
import { Icon } from '@/core/ui/icons';
import { api } from '@/core/api/apiClient';
import { useLookupStore } from '@/features/lookups/store/lookupStore';
import { useAreaStore } from '@/features/areas/store/areaStore';
import { useAuthStore } from '@/features/auth/store/authStore';
import { initials, avatarColor } from '@/core/ui/helpers';
import { Spinner } from '@/core/ui/atoms';

export function MembersModal({ area, onClose }: { area: Area; onClose: () => void }) {
  const user = useAuthStore((s) => s.user);
  const lookup = useLookupStore();
  const loadLookups = useLookupStore((s) => s.load);
  const [members, setMembers] = useState<Assignee[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canManage = !!user && (user.isAdmin || area.managerId === user.uid);
  // A co-lead (v1.99.0) adds and removes people; the board's switches stay the lead's.
  const canChangeMembers = canManage || (!!user && (area.coLeadIds ?? []).includes(user.uid));

  useEffect(() => {
    void loadLookups();
    void (async () => {
      try {
        const { members } = await api.get<{ members: Assignee[] }>(`/areas/${area.id}/members`);
        setMembers(members);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load members.');
      }
    })();
  }, [area.id, loadLookups]);

  const available = useMemo(
    () => (members ? lookup.assignees.filter((a) => !members.some((m) => m.id === a.id)) : []),
    [lookup.assignees, members],
  );

  // The 🔒 badge reads memberCount off the areas STORE, which this modal doesn't
  // otherwise touch — so adding someone to a private project left the header still
  // claiming "only its one member" until a full reload. The response already
  // carries the new list, so keep the store honest from it.
  const syncCount = (list: Assignee[]) =>
    useAreaStore.getState().applyAreaUpdate({ ...area, memberCount: list.length });

  const add = async (uid: number) => {
    setBusy(true); setError(null);
    try {
      const { members } = await api.post<{ members: Assignee[] }>(`/areas/${area.id}/members`, { userId: uid });
      setMembers(members);
      syncCount(members);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not add member.');
    }
    setBusy(false);
  };

  const [isPublic, setIsPublic] = useState(!!area.isPublic);
  const setPublic = async (next: boolean) => {
    if (next && !window.confirm(`Let everyone at CG see "${area.name}"? They'll be able to open it. They won't be added as members, so nobody starts getting its notifications.`)) return;
    setBusy(true); setError(null);
    try {
      const { area: updated } = await api.put<{ area: Area }>(`/areas/${area.id}/public`, { public: next });
      setIsPublic(!!updated.isPublic);
      useAreaStore.getState().applyAreaUpdate(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not change who can see this project.');
    }
    setBusy(false);
  };

  // v1.89.0: managers can see the projects of the people under them on the org
  // chart (look only). This is the exception — keep a board to its members.
  const [reportsVisible, setReportsVisible] = useState(area.reportsVisible !== false);
  const setReports = async (next: boolean) => {
    setBusy(true); setError(null);
    try {
      const { area: updated } = await api.put<{ area: Area }>(`/areas/${area.id}/reports-visible`, { visible: next });
      setReportsVisible(updated.reportsVisible !== false);
      useAreaStore.getState().applyAreaUpdate(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not change who can see this project.');
    }
    setBusy(false);
  };

  // v1.90.0: every board takes walk-in requests ("Ask a team") unless the lead
  // closes it. Filed cards land in the request column; the filer sees only
  // their own card.
  const [acceptsRequests, setAcceptsRequests] = useState(area.acceptsRequests !== false);
  const setAccepts = async (next: boolean) => {
    setBusy(true); setError(null);
    try {
      const { area: updated } = await api.put<{ area: Area }>(`/areas/${area.id}/accepts-requests`, { accepts: next });
      setAcceptsRequests(updated.acceptsRequests !== false);
      useAreaStore.getState().applyAreaUpdate(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not change that.');
    }
    setBusy(false);
  };

  // v1.100.0: the lead (or an admin) makes a member a co-lead from here.
  const [coLeadIds, setCoLeadIds] = useState<number[]>(area.coLeadIds ?? []);
  const setCoLead = async (uid: number, next: boolean) => {
    setBusy(true); setError(null);
    try {
      const { area: updated } = await api.put<{ area: Area }>(`/areas/${area.id}/co-leads/${uid}`, { coLead: next });
      setCoLeadIds(updated.coLeadIds ?? []);
      useAreaStore.getState().applyAreaUpdate(updated);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not change that.');
    }
    setBusy(false);
  };

  const remove = async (uid: number) => {
    setBusy(true); setError(null);
    try {
      const { members } = await api.delete<{ members: Assignee[] }>(`/areas/${area.id}/members/${uid}`);
      setMembers(members);
      syncCount(members);
      setCoLeadIds((ids) => ids.filter((i) => i !== uid));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not remove member.');
    }
    setBusy(false);
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50 animate-fade-in" onClick={onClose} />
      <div className="fixed left-1/2 top-1/2 z-50 w-[440px] max-w-[94vw] -translate-x-1/2 -translate-y-1/2 rounded-xl border border-line bg-canvas shadow-xl animate-fade-in">
        <div className="flex items-center justify-between border-b border-line px-4 py-3">
          <div>
            <div className="text-sm font-semibold text-ink">Members · {area.name}</div>
            <div className="text-xs text-faint">Who can see this project</div>
          </div>
          <button onClick={onClose} className="rounded p-1 text-muted hover:bg-hover hover:text-ink"><Icon i={X} size={14} /></button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto px-4 py-3">
          {error && <div className="mb-2 rounded-md border border-danger/30 bg-danger/10 px-2 py-1 text-xs text-danger">{error}</div>}

          {members === null ? (
            <Spinner label="Loading members…" />
          ) : (
            <>
              <div className="space-y-1">
                {members.map((m) => (
                  <div key={m.id} className="flex items-center gap-2 rounded-md px-1 py-1">
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-medium text-white" style={{ backgroundColor: avatarColor(m.name) }}>{initials(m.name)}</span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[13px] text-ink">{m.name}{m.id === area.managerId && <span className="ml-1 text-[10px] text-brand">Lead</span>}{coLeadIds.includes(m.id) && <span className="ml-1 text-[10px] text-brand">Co-lead</span>}</div>
                      {m.email && <div className="truncate text-[11px] text-faint">{m.email}</div>}
                    </div>
                    {canManage && m.id !== area.managerId && (
                      <button
                        disabled={busy}
                        onClick={() => void setCoLead(m.id, !coLeadIds.includes(m.id))}
                        className="shrink-0 rounded border border-line px-1.5 py-0.5 text-[11px] text-muted transition hover:bg-hover hover:text-ink disabled:opacity-40"
                        title={coLeadIds.includes(m.id) ? 'They stop adding and removing people here' : 'They can add and remove people on this board'}
                      >
                        {coLeadIds.includes(m.id) ? 'Not co-lead' : 'Make co-lead'}
                      </button>
                    )}
                    {canChangeMembers && m.id !== area.managerId && (
                      <button disabled={busy} onClick={() => void remove(m.id)} className="rounded px-1.5 text-faint hover:text-danger disabled:opacity-40" title="Remove"><Icon i={X} size={14} /></button>
                    )}
                  </div>
                ))}
                {members.length === 0 && <div className="py-2 text-xs text-faint">No members yet.</div>}
              </div>

              {canManage && (
                <div className="mt-2 text-[11px] text-faint">A co-lead can add and remove people on this board. Only the lead changes its settings.</div>
              )}
              {canChangeMembers && (
                <div className="mt-3 border-t border-line pt-3">
                  <label className="mb-1 block text-xs text-faint">Add someone</label>
                  <select
                    value=""
                    disabled={busy || available.length === 0}
                    onChange={(e) => { if (e.target.value) void add(Number(e.target.value)); }}
                    className="w-full rounded-md border border-line bg-canvas px-2 py-1.5 text-sm text-ink outline-none focus:border-brand disabled:opacity-50"
                  >
                    <option value="">{available.length ? '+ Add a person…' : 'Everyone is already a member'}</option>
                    {available.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
              )}
              {canManage && (
                <div className="mt-3">
                  {/* Azriella: keep a project to yourself while you build it, then
                      open it up when it's ready. This grants VIEW, not membership —
                      so making it public doesn't sign everyone up for its
                      notifications, and turning it off again costs nothing. */}
                  <div className="mt-3 rounded-md border border-line px-2.5 py-2">
                    <div className="text-xs font-medium text-ink">
                      {isPublic ? <><Icon i={Globe} size={14} className="inline align-[-0.2em]" /> Everyone at CG can see this</> : members.length <= 1 ? <><Icon i={Lock} size={14} className="inline align-[-0.2em]" /> Private to you</> : `Visible to its ${members.length} members`}
                    </div>
                    <div className="mt-0.5 text-[11px] text-faint">
                      {isPublic
                        ? 'Anyone can open it. Only the members above get its notifications.'
                        : 'Only the people above can open it (plus workspace admins).'}
                    </div>
                    <button
                      disabled={busy}
                      onClick={() => void setPublic(!isPublic)}
                      className="mt-2 rounded-md border border-line px-2.5 py-1 text-xs font-medium text-ink transition hover:bg-hover disabled:opacity-40"
                    >
                      {busy ? 'Saving…' : isPublic ? 'Make it private again' : <><Icon i={Globe} size={14} className="inline align-[-0.2em]" /> Let everyone at CG see it</>}
                    </button>
                  </div>
                  <div className="mt-2 rounded-md border border-line px-2.5 py-2">
                    <div className="text-xs font-medium text-ink">
                      {acceptsRequests ? 'Takes requests from anyone at CG' : 'Not taking requests'}
                    </div>
                    <div className="mt-0.5 text-[11px] text-faint">
                      {acceptsRequests
                        ? 'Anyone can send this team a request from \u201cAsk a team\u201d. It lands here as a card; they see only their own card and hear back when it\u2019s taken, done or declined.'
                        : 'Only the people above can add cards here. \u201cAsk a team\u201d won\u2019t offer this board.'}
                    </div>
                    <button
                      disabled={busy}
                      onClick={() => void setAccepts(!acceptsRequests)}
                      className="mt-2 rounded-md border border-line px-2.5 py-1 text-xs font-medium text-ink transition hover:bg-hover disabled:opacity-40"
                    >
                      {busy ? 'Saving…' : acceptsRequests ? 'Stop taking requests' : 'Take requests again'}
                    </button>
                  </div>
                  {!isPublic && (
                    <div className="mt-2 rounded-md border border-line px-2.5 py-2">
                      <div className="text-xs font-medium text-ink">
                        {reportsVisible ? 'The members\u2019 managers can look' : 'Kept to its members'}
                      </div>
                      <div className="mt-0.5 text-[11px] text-faint">
                        {reportsVisible
                          ? 'Anyone a member reports to on the org chart can open this and look. They can\u2019t change anything unless they\u2019re added above.'
                          : 'Managers of its members can\u2019t open this. Only the people above can (plus workspace admins).'}
                      </div>
                      <button
                        disabled={busy}
                        onClick={() => void setReports(!reportsVisible)}
                        className="mt-2 rounded-md border border-line px-2.5 py-1 text-xs font-medium text-ink transition hover:bg-hover disabled:opacity-40"
                      >
                        {busy ? 'Saving…' : reportsVisible ? 'Keep it to its members' : 'Let their managers look'}
                      </button>
                    </div>
                  )}
                </div>
              )}
              {!canChangeMembers && (
                <div className="mt-3 border-t border-line pt-3 text-xs text-faint">
                  {/* Never name a lead who has left — "go ask Jarvis" sent people
                      chasing an ex-employee across 11 live projects (9/8). */}
                  {area.managerName && area.managerActive !== false
                    ? <>Only the project lead ({area.managerName}) or an admin can change members.</>
                    : <>This project has no lead right now — ask an admin to change who&rsquo;s on it.</>}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
